// src/components/character_editor/sections/AppearanceEditSection.jsx
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Divider,
  Button
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Face as FaceIcon,
  Height as HeightIcon,
  FitnessCenter as FitnessCenterIcon,
  ColorLens as ColorLensIcon
} from '@mui/icons-material';

const AppearanceEditSection = ({ character, onAppearanceChange }) => {
  const appearance = character.appearance || {};

  const handleAppearanceChange = (field, value) => {
    onAppearanceChange({ [field]: value });
  };

  // Опции для выбора
  const skinTones = [
    'Бледный', 'Светлый', 'Средний', 'Оливковый', 'Смуглый', 'Темный', 'Очень темный'
  ];

  const hairColors = [
    'Черный', 'Темно-коричневый', 'Коричневый', 'Русый', 'Блондин', 'Рыжий', 'Седой', 'Белый'
  ];

  const eyeColors = [
    'Карие', 'Темно-карие', 'Ореховые', 'Зеленые', 'Голубые', 'Серые', 'Фиолетовые', 'Красные'
  ];

  const buildTypes = [
    'Худощавый', 'Стройный', 'Средний', 'Мускулистый', 'Плотный', 'Коренастый'
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <PaletteIcon sx={{ mr: 2 }} />
        Внешность персонажа
      </Typography>

      <Grid container spacing={3}>
        {/* Аватар и основная информация */}
        <Grid item xs={12} md={4}>
          <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '3rem'
                }}
              >
                <FaceIcon fontSize="inherit" />
              </Avatar>
              
              <Typography variant="h6" gutterBottom>
                {character.name}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                {appearance.height && (
                  <Chip label={`Рост: ${appearance.height} см`} variant="outlined" />
                )}
                {appearance.build && (
                  <Chip label={`Телосложение: ${appearance.build}`} variant="outlined" />
                )}
                {appearance.age && (
                  <Chip label={`Возраст: ${appearance.age} лет`} variant="outlined" />
                )}
              </Box>

              <Button 
                variant="outlined" 
                fullWidth 
                sx={{ mt: 2 }}
                disabled
              >
                Загрузить изображение (скоро)
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Основные параметры внешности */}
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <FaceIcon sx={{ mr: 1 }} />
                Основные параметры
              </Typography>

              <Grid container spacing={3}>
                {/* Возраст */}
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>
                    Возраст: {appearance.age || 25} лет
                  </Typography>
                  <Slider
                    value={appearance.age || 25}
                    onChange={(_, value) => handleAppearanceChange('age', value)}
                    min={16}
                    max={100}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 16, label: '16' },
                      { value: 30, label: '30' },
                      { value: 50, label: '50' },
                      { value: 80, label: '80' }
                    ]}
                  />
                </Grid>

                {/* Рост */}
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <HeightIcon sx={{ mr: 1, fontSize: 20 }} />
                    Рост: {appearance.height || 175} см
                  </Typography>
                  <Slider
                    value={appearance.height || 175}
                    onChange={(_, value) => handleAppearanceChange('height', value)}
                    min={120}
                    max={220}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 120, label: '120' },
                      { value: 160, label: '160' },
                      { value: 180, label: '180' },
                      { value: 200, label: '200' }
                    ]}
                  />
                </Grid>

                {/* Вес */}
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <FitnessCenterIcon sx={{ mr: 1, fontSize: 20 }} />
                    Вес: {appearance.weight || 70} кг
                  </Typography>
                  <Slider
                    value={appearance.weight || 70}
                    onChange={(_, value) => handleAppearanceChange('weight', value)}
                    min={40}
                    max={150}
                    valueLabelDisplay="auto"
                  />
                </Grid>

                {/* Телосложение */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Телосложение</InputLabel>
                    <Select
                      value={appearance.build || ''}
                      onChange={(e) => handleAppearanceChange('build', e.target.value)}
                      label="Телосложение"
                    >
                      {buildTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Цветовые характеристики */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <ColorLensIcon sx={{ mr: 1 }} />
                Цветовые характеристики
              </Typography>

              <Grid container spacing={3}>
                {/* Цвет кожи */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Цвет кожи</InputLabel>
                    <Select
                      value={appearance.skinTone || ''}
                      onChange={(e) => handleAppearanceChange('skinTone', e.target.value)}
                      label="Цвет кожи"
                    >
                      {skinTones.map(tone => (
                        <MenuItem key={tone} value={tone}>
                          {tone}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Цвет волос */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Цвет волос</InputLabel>
                    <Select
                      value={appearance.hairColor || ''}
                      onChange={(e) => handleAppearanceChange('hairColor', e.target.value)}
                      label="Цвет волос"
                    >
                      {hairColors.map(color => (
                        <MenuItem key={color} value={color}>
                          {color}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Цвет глаз */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Цвет глаз</InputLabel>
                    <Select
                      value={appearance.eyeColor || ''}
                      onChange={(e) => handleAppearanceChange('eyeColor', e.target.value)}
                      label="Цвет глаз"
                    >
                      {eyeColors.map(color => (
                        <MenuItem key={color} value={color}>
                          {color}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Дополнительные детали */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Дополнительные детали
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Стиль прически"
                    value={appearance.hairStyle || ''}
                    onChange={(e) => handleAppearanceChange('hairStyle', e.target.value)}
                    placeholder="Опишите прическу персонажа..."
                    helperText="Например: короткие волосы, длинные косы, ирокез и т.д."
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Особые приметы"
                    value={appearance.distinctiveFeatures || ''}
                    onChange={(e) => handleAppearanceChange('distinctiveFeatures', e.target.value)}
                    placeholder="Шрамы, татуировки, родинки и другие особенности..."
                    helperText="Что делает внешность персонажа уникальной"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Общее описание внешности"
                    value={appearance.description || ''}
                    onChange={(e) => handleAppearanceChange('description', e.target.value)}
                    placeholder="Подробно опишите внешность вашего персонажа..."
                    helperText="Это описание будет использоваться в игровых сценах"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Стиль одежды */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ bgcolor: 'background.default' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Стиль и одежда
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Предпочитаемый стиль одежды"
                value={appearance.clothingStyle || ''}
                onChange={(e) => handleAppearanceChange('clothingStyle', e.target.value)}
                placeholder="Опишите типичную одежду и аксессуары персонажа..."
                helperText="Как ваш персонаж обычно одевается?"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AppearanceEditSection;